import type { LineMode, TrackPoint } from '../types';

const DEFAULT_INTERVAL_MINUTES = 10;
export const FULL_FILE_TIME_MARKER_INTERVAL_MINUTES = 10;
export const PARTIAL_TRACK_TIME_MARKER_INTERVAL_MINUTES = 5;
export const START_TIME_MARKER_INTERVAL_MINUTES = 0.5;
export const START_TIME_MARKER_LABEL_OFFSET_MINUTES = -3;
const MS_PER_MINUTE = 60 * 1000;
const MS_PER_HOUR = 60 * MS_PER_MINUTE;
const DEGREE_LABEL = '\u00B0';

export type TrackSelectionType = 'full' | 'segment' | 'start';

export interface TenMinuteMarker {
  point: TrackPoint;
  label: string;
  timestampMs: number;
  targetTimestampMs: number;
  offsetMs: number;
}

interface TenMinuteMarkerOptions {
  trackData: TrackPoint[];
  mode: LineMode;
  minDelta?: number;
  labelOffsetMinutes?: number;
}

const VISIBLE_TEN_MINUTE_MODES = new Set<LineMode>(['plain', 'speed', 'tactical']);

export const isFiniteTrackNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

export const getTimeMarkerIntervalMinutes = (selectionType?: TrackSelectionType) =>
  selectionType === 'start'
    ? START_TIME_MARKER_INTERVAL_MINUTES
    : selectionType === 'full'
    ? FULL_FILE_TIME_MARKER_INTERVAL_MINUTES
    : PARTIAL_TRACK_TIME_MARKER_INTERVAL_MINUTES;

export const getTimeMarkerLabelOffsetMinutes = (selectionType?: TrackSelectionType) =>
  selectionType === 'start' ? START_TIME_MARKER_LABEL_OFFSET_MINUTES : 0;

const parseTrackTimestamp = (point: TrackPoint) => {
  const timestamp = new Date(point.utc ?? point.date ?? '').getTime();
  return Number.isFinite(timestamp) ? timestamp : null;
};

const hasFiniteCoordinates = (point: TrackPoint) =>
  Number.isFinite(point.lat) && Number.isFinite(point.lon);

export const formatElapsedDuration = (elapsedMs: number, useHourFormat: boolean) => {
  const isNegative = elapsedMs < 0;
  const totalSeconds = Math.max(0, Math.round(Math.abs(elapsedMs) / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const minutesLabel = String(minutes).padStart(2, '0');
  const secondsLabel = String(seconds).padStart(2, '0');
  const sign = isNegative ? '-' : '';

  if (useHourFormat || hours > 0) {
    return `${sign}${String(hours).padStart(2, '0')}:${minutesLabel}:${secondsLabel}`;
  }

  return `${sign}${minutesLabel}:${secondsLabel}`;
};

const formatOneDecimalOrMissing = (value: unknown) =>
  isFiniteTrackNumber(value) ? value.toFixed(1) : '--';

const getMarkerLabel = (
  point: TrackPoint,
  mode: LineMode,
  elapsedMs: number,
  useHourFormat: boolean,
) => {
  if (mode === 'plain') {
    return formatElapsedDuration(elapsedMs, useHourFormat);
  }

  return `${formatOneDecimalOrMissing(point.twd)}${DEGREE_LABEL} / ${formatOneDecimalOrMissing(
    point.tws,
  )} kt`;
};

export const getTenMinuteMarkers = ({
  trackData,
  mode,
  minDelta = DEFAULT_INTERVAL_MINUTES,
  labelOffsetMinutes = 0,
}: TenMinuteMarkerOptions): TenMinuteMarker[] => {
  if (!VISIBLE_TEN_MINUTE_MODES.has(mode) || trackData.length === 0) {
    return [];
  }

  const intervalMs = minDelta * MS_PER_MINUTE;
  if (!Number.isFinite(intervalMs) || intervalMs <= 0) {
    return [];
  }

  const validPoints = trackData
    .map((point, originalIndex) => {
      const timestampMs = parseTrackTimestamp(point);
      return timestampMs === null || !hasFiniteCoordinates(point)
        ? null
        : { point, originalIndex, timestampMs };
    })
    .filter(
      (
        item,
      ): item is {
        point: TrackPoint;
        originalIndex: number;
        timestampMs: number;
      } => item !== null,
    )
    .sort((a, b) => a.timestampMs - b.timestampMs || a.originalIndex - b.originalIndex);

  const usedPointIndexes = new Set<number>();
  const markers: TenMinuteMarker[] = [];
  const startTimestampMs = validPoints[0]?.timestampMs;
  const endTimestampMs = validPoints.at(-1)?.timestampMs;
  if (startTimestampMs === undefined || endTimestampMs === undefined) {
    return [];
  }
  const maxTargetDistanceMs = intervalMs / 2;
  const useHourFormat = endTimestampMs - startTimestampMs >= MS_PER_HOUR;
  const labelOffsetMs = Number.isFinite(labelOffsetMinutes)
    ? labelOffsetMinutes * MS_PER_MINUTE
    : 0;

  for (
    let targetTimestampMs = startTimestampMs;
    targetTimestampMs <= endTimestampMs;
    targetTimestampMs += intervalMs
  ) {
    let closest:
      | {
          point: TrackPoint;
          originalIndex: number;
          timestampMs: number;
          offsetMs: number;
        }
      | null = null;

    for (const item of validPoints) {
      if (usedPointIndexes.has(item.originalIndex)) {
        continue;
      }
      const offsetMs = Math.abs(item.timestampMs - targetTimestampMs);
      const itemIsAtOrAfterTarget = item.timestampMs >= targetTimestampMs;
      const closestIsAtOrAfterTarget = closest
        ? closest.timestampMs >= targetTimestampMs
        : false;
      if (
        closest === null ||
        offsetMs < closest.offsetMs ||
        (offsetMs === closest.offsetMs &&
          (itemIsAtOrAfterTarget === closestIsAtOrAfterTarget
            ? item.timestampMs < closest.timestampMs
            : itemIsAtOrAfterTarget))
      ) {
        closest = { ...item, offsetMs };
      }
    }

    if (!closest || closest.offsetMs > maxTargetDistanceMs) {
      continue;
    }
    usedPointIndexes.add(closest.originalIndex);
    markers.push({
      point: closest.point,
      label: getMarkerLabel(
        closest.point,
        mode,
        targetTimestampMs - startTimestampMs + labelOffsetMs,
        useHourFormat,
      ),
      timestampMs: closest.timestampMs,
      targetTimestampMs,
      offsetMs: closest.offsetMs,
    });
  }

  return markers;
};
